import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum,
  UpdateInternalAuditReportMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type for updateInternalAuditReport. Report has no schedule, so no
 * schedule transformation is needed. The legacy action returns affected_rows
 * (NOT an IdOutput) and takes no OriginalTimestamp — no optimistic lock.
 */
type UpdateInternalAuditReportTRPCInput = {
  Id: string;
  Title: string;
  Status: Assessment_Status_Enum;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  affected_rows: number;
}): UpdateInternalAuditReportMutation => {
  return {
    updateInternalAuditReportApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: trpcData.affected_rows,
    },
  };
};

export const useUpdateInternalAuditReportTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditReport.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.internalAuditReport.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.internalAuditReport.internalAuditReportById.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `internal_audit_entity` and
        // the assessment-result fields (their ratings roll up into the report).
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditEntity.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.documentAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.obligationAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.riskAssessmentResult.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateInternalAuditReport: async (
      variables: UpdateInternalAuditReportTRPCInput
    ): Promise<UpdateInternalAuditReportMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
